import { useEffect, useRef, useState, useCallback } from "react";
import WaveSurfer from "wavesurfer.js";
import RegionsPlugin from "wavesurfer.js/dist/plugins/regions.js";
import * as Tone from "tone";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Play,
  Pause,
  Rewind,
  FastForward,
  RotateCcw,
  Repeat,
} from "lucide-react";

interface AudioPracticePlayerProps {
  audioUrl: string;
  fileName?: string;
  fileType?: string;
  initialTempo?: number;
  initialPitch?: number;
  initialLoopStart?: number | null;
  initialLoopEnd?: number | null;
  onSettingsChange?: (settings: {
    tempo: number;
    pitch: number;
    loopStart: number | null;
    loopEnd: number | null;
  }) => void;
}

function formatTime(seconds: number): string {
  if (!isFinite(seconds) || isNaN(seconds)) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

// Granular synthesis tuning for Tone.GrainPlayer (tempo/pitch engine).
// Smaller grainSize = more responsive but more "granular" artifacts;
// larger overlap = smoother crossfades but more smearing. Tune by ear.
const GRAIN_SIZE = 0.2;
const GRAIN_OVERLAP = 0.1;

export function AudioPracticePlayer({
  audioUrl,
  fileName,
  fileType,
  initialTempo = 1,
  initialPitch = 0,
  initialLoopStart = null,
  initialLoopEnd = null,
  onSettingsChange,
}: AudioPracticePlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const wsRef = useRef<WaveSurfer | null>(null);
  const regionsRef = useRef<ReturnType<typeof RegionsPlugin.create> | null>(null);
  const regionRef = useRef<any>(null);

  // Tempo/pitch playback engine: 'native' (default, <audio> + WSOLA) or
  // 'grain' (Tone.GrainPlayer, true independent tempo/pitch). One-way
  // upgrade — switches to 'grain' the first time tempo!=1 or pitch!=0.
  const grainPlayerRef = useRef<Tone.GrainPlayer | null>(null);
  const decodedBufferRef = useRef<AudioBuffer | null>(null);
  const engineModeRef = useRef<"native" | "grain">("native");
  const engineSwitchInFlightRef = useRef(false);
  const decodeFallbackWarnedRef = useRef(false);
  const engineSwitchErrorWarnedRef = useRef(false);

  // Position tracking for grain mode (buffer-seconds). playStartRateRef must
  // reflect the rate that was active during [playStartCtxTimeRef, now).
  const playStartCtxTimeRef = useRef(0);
  const playStartOffsetRef = useRef(0);
  const playStartRateRef = useRef(1);
  const grainPositionRef = useRef(0);
  const rafIdRef = useRef<number | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [tempo, setTempo] = useState(initialTempo);
  const [pitch, setPitch] = useState(initialPitch);
  const [loopStart, setLoopStart] = useState<number | null>(initialLoopStart);
  const [loopEnd, setLoopEnd] = useState<number | null>(initialLoopEnd);
  const [loopEnabled, setLoopEnabled] = useState(
    initialLoopStart !== null && initialLoopEnd !== null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const isReadyRef = useRef(false);

  const loopStartRef = useRef(loopStart);
  const loopEndRef = useRef(loopEnd);
  const loopEnabledRef = useRef(loopEnabled);
  loopStartRef.current = loopStart;
  loopEndRef.current = loopEnd;
  loopEnabledRef.current = loopEnabled;

  const tempoRef = useRef(tempo);
  tempoRef.current = tempo;
  const pitchRef = useRef(pitch);
  pitchRef.current = pitch;

  const applyTempo = useCallback((ws: WaveSurfer, t: number) => {
    const audio = ws.getMediaElement() as HTMLAudioElement;
    audio.preservesPitch = true;
    ws.setPlaybackRate(t, true);
  }, []);

  // Reuse WaveSurfer's already-decoded buffer to build a GrainPlayer without
  // a second fetch/decode. Returns null if decoding hasn't completed (iOS
  // fallback paths) — caller should fall back to native playbackRate.
  const getOrCreateGrainPlayer = useCallback((): Tone.GrainPlayer | null => {
    if (grainPlayerRef.current) return grainPlayerRef.current;
    const ws = wsRef.current;
    if (!ws) return null;

    const buf = decodedBufferRef.current ?? ws.getDecodedData();
    decodedBufferRef.current = buf;
    if (!buf) return null;

    const gp = new Tone.GrainPlayer({
      url: buf,
      grainSize: GRAIN_SIZE,
      overlap: GRAIN_OVERLAP,
      playbackRate: tempoRef.current,
      detune: pitchRef.current * 100,
      loop: false,
    }).toDestination();

    grainPlayerRef.current = gp;
    return gp;
  }, []);

  const stopRafLoop = useCallback(() => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
  }, []);

  const stopGrainPlayback = useCallback((freezeAt?: number) => {
    grainPlayerRef.current?.stop();
    if (freezeAt !== undefined) grainPositionRef.current = freezeAt;
    setIsPlaying(false);
    stopRafLoop();
    wsRef.current?.pause();
  }, [stopRafLoop]);

  const startGrainPlayback = useCallback((fromPosition: number) => {
    const gp = grainPlayerRef.current;
    if (!gp) return;
    const dur = decodedBufferRef.current?.duration ?? 0;
    const offset = Math.max(0, Math.min(fromPosition, dur));

    gp.playbackRate = tempoRef.current;
    gp.detune = pitchRef.current * 100;
    gp.start(undefined, offset);

    playStartCtxTimeRef.current = Tone.now();
    playStartOffsetRef.current = offset;
    playStartRateRef.current = gp.playbackRate;
    grainPositionRef.current = offset;

    setIsPlaying(true);
    startRafLoop();
  }, []);

  // rAF loop reads everything via refs, so it's stable across renders and
  // never needs recreating mid-playback.
  const startRafLoop = useCallback(() => {
    if (rafIdRef.current !== null) return;
    const tick = () => {
      const gp = grainPlayerRef.current;
      const ws = wsRef.current;
      if (!gp || !ws || engineModeRef.current !== "grain" || gp.state !== "started") {
        rafIdRef.current = null;
        return;
      }
      let pos = playStartOffsetRef.current
        + (Tone.now() - playStartCtxTimeRef.current) * playStartRateRef.current;
      const dur = decodedBufferRef.current?.duration ?? Infinity;

      if (
        loopEnabledRef.current &&
        loopStartRef.current !== null &&
        loopEndRef.current !== null &&
        pos >= loopEndRef.current
      ) {
        startGrainPlayback(loopStartRef.current);
        pos = loopStartRef.current;
      } else if (pos >= dur) {
        stopGrainPlayback(0);
        ws.setTime(0);
        setCurrentTime(0);
        return;
      }

      grainPositionRef.current = pos;
      ws.setTime(pos);
      setCurrentTime(pos);
      rafIdRef.current = requestAnimationFrame(tick);
    };
    rafIdRef.current = requestAnimationFrame(tick);
  }, [startGrainPlayback, stopGrainPlayback]);

  function rebasePositionTracking(gp: Tone.GrainPlayer) {
    if (gp.state === "started") {
      const pos = playStartOffsetRef.current
        + (Tone.now() - playStartCtxTimeRef.current) * playStartRateRef.current;
      playStartOffsetRef.current = pos;
      playStartCtxTimeRef.current = Tone.now();
    }
    playStartRateRef.current = gp.playbackRate;
  }

  const seekGrainTo = useCallback((pos: number) => {
    const ws = wsRef.current;
    const dur = decodedBufferRef.current?.duration ?? duration;
    const clamped = Math.max(0, Math.min(pos, dur));
    if (isPlaying) {
      startGrainPlayback(clamped);
    } else {
      grainPositionRef.current = clamped;
      playStartOffsetRef.current = clamped;
    }
    ws?.setTime(clamped);
    setCurrentTime(clamped);
  }, [isPlaying, duration, startGrainPlayback]);

  const seekGrainToRef = useRef(seekGrainTo);
  seekGrainToRef.current = seekGrainTo;

  // Touch-scrub helper: maps a Touch's clientX to a playback position and
  // seeks via the active engine. Called by native touchstart + React
  // onTouchMove/onTouchEnd handlers on the waveform container.
  const seekFromTouch = (touch: Touch | React.Touch) => {
    const el = containerRef.current;
    if (!el || !duration) return;
    const rect = el.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (touch.clientX - rect.left) / rect.width));
    const t = pct * duration;
    if (engineModeRef.current === "grain") {
      seekGrainToRef.current(t);
    } else {
      wsRef.current?.setTime(t);
    }
  };
  const seekFromTouchRef = useRef(seekFromTouch);
  seekFromTouchRef.current = seekFromTouch;

  // First time tempo!=1 or pitch!=0: switch to Tone.GrainPlayer for the rest
  // of this track's session (one-way upgrade, mirrors the prior lazy
  // PitchShift pattern). WaveSurfer's <audio> becomes visualization-only.
  const switchToGrainEngine = useCallback(async () => {
    if (engineModeRef.current === "grain") return;
    if (engineSwitchInFlightRef.current) return;
    engineSwitchInFlightRef.current = true;
    try {
      const ws = wsRef.current;
      if (!ws) return;

      const gp = getOrCreateGrainPlayer();
      if (!gp) {
        if (!decodeFallbackWarnedRef.current) {
          decodeFallbackWarnedRef.current = true;
          console.warn(
            "GrainPlayer unavailable (no decoded audio buffer) — falling back " +
            "to native playbackRate for tempo; pitch changes will have no effect."
          );
        }
        return;
      }

      await Tone.start();

      const wasPlaying = isPlaying;
      const currentPos = ws.getCurrentTime();

      // Flip the mode before touching playback so the 'pause' handler
      // (gated on engineModeRef) doesn't mistake this for a native pause.
      engineModeRef.current = "grain";

      // Keep element unmuted at near-inaudible volume so iOS maintains the
      // Playback audio session. audio.muted=true would release it; 0.001
      // (-60dB) is inaudible but counts as active media for the OS session.
      const mediaEl = ws.getMediaElement() as HTMLAudioElement;
      mediaEl.loop = true;
      ws.setVolume(0.001);

      if (wasPlaying) {
        startGrainPlayback(currentPos);
      } else {
        grainPositionRef.current = currentPos;
        playStartOffsetRef.current = currentPos;
        playStartRateRef.current = tempoRef.current;
      }
    } finally {
      engineSwitchInFlightRef.current = false;
    }
  }, [getOrCreateGrainPlayer, isPlaying, startGrainPlayback]);

  useEffect(() => {
    if (!containerRef.current) return;

    setIsLoading(true);
    setLoadError(null);
    setIsReady(false);
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);

    const regions = RegionsPlugin.create();
    regionsRef.current = regions;

    const ws = WaveSurfer.create({
      container: containerRef.current,
      waveColor: "rgba(99, 102, 241, 0.4)",
      progressColor: "rgba(99, 102, 241, 0.9)",
      cursorColor: "rgba(255,255,255,0.7)",
      cursorWidth: 2,
      height: 100,
      barWidth: 2,
      barGap: 1,
      barRadius: 2,
      normalize: true,
      interact: true,
      plugins: [regions],
    });
    wsRef.current = ws;

    const markReady = (dur: number) => {
      if (isReadyRef.current) return;
      isReadyRef.current = true;
      setIsLoading(false);
      setIsReady(true);
      setDuration(dur);
      applyTempo(ws, initialTempo);

      if (initialLoopStart !== null && initialLoopEnd !== null) {
        const region = regions.addRegion({
          start: initialLoopStart,
          end: initialLoopEnd,
          color: "rgba(99, 102, 241, 0.25)",
          drag: true,
          resize: true,
        });
        regionRef.current = region;
      }
    };

    ws.on("ready", () => markReady(ws.getDuration()));

    // In 'grain' mode, the <audio> element is muted/visualization-only and
    // playback state is managed directly by the grain helpers + rAF loop.
    ws.on("play", () => {
      if (engineModeRef.current === "native") setIsPlaying(true);
    });
    ws.on("pause", () => {
      if (engineModeRef.current === "native") setIsPlaying(false);
    });
    ws.on("finish", () => {
      if (engineModeRef.current === "native") {
        setIsPlaying(false);
        setCurrentTime(0);
      }
    });

    ws.on("timeupdate", (t) => {
      if (engineModeRef.current !== "native") return;
      setCurrentTime(t);
      if (loopEnabledRef.current && loopStartRef.current !== null && loopEndRef.current !== null) {
        if (t >= loopEndRef.current) {
          ws.setTime(loopStartRef.current);
        }
      }
    });

    // Click-to-seek on the waveform. In native mode WaveSurfer already
    // handles its own seek; in grain mode we drive the GrainPlayer.
    // (seekGrainToRef avoids needing this effect to depend on seekGrainTo,
    // whose identity changes with isPlaying/duration.)
    ws.on("interaction", (t: number) => {
      if (engineModeRef.current === "grain") seekGrainToRef.current(t);
    });

    // Non-passive touchstart so we can call preventDefault() and prevent the
    // page from scrolling while the user scrubs the waveform on touch devices.
    const handleTouchStart = (e: TouchEvent) => {
      e.preventDefault();
      if (e.touches[0]) seekFromTouchRef.current(e.touches[0]);
    };
    containerRef.current?.addEventListener("touchstart", handleTouchStart, { passive: false });

    // WaveSurfer's decodeAudioData can fail for valid files (iOS Safari + MP3/M4A).
    // Just log it — the canplay fallback below will still unblock the player.
    ws.on("error", (err: any) => {
      console.warn("WaveSurfer decode error (non-fatal, canplay fallback active):", err);
    });

    regions.on("region-updated", (region: any) => {
      regionRef.current = region;
      setLoopStart(region.start);
      setLoopEnd(region.end);
    });

    ws.load(audioUrl).catch(() => {
      // Decode errors are already handled via the "error" listener above
      // and the canplay/timeout fallbacks below.
    });

    // Attach canplay fallback immediately after load() so it catches both:
    // 1) iOS Safari where decodeAudioData hangs (M4A/AAC)
    // 2) Cases where decodeAudioData errors but the <audio> element still works
    // markReady() is idempotent so it won't double-fire if "ready" also fires.
    const fallbackTimer = setTimeout(() => {
      const mediaEl = ws.getMediaElement() as HTMLAudioElement | null;
      if (!mediaEl) return;
      const onReady = () => markReady(mediaEl.duration || 0);
      mediaEl.addEventListener("canplay", onReady, { once: true });
      mediaEl.addEventListener("loadedmetadata", onReady, { once: true });
      if (mediaEl.readyState >= 1 /* HAVE_METADATA */) {
        onReady();
      } else if (mediaEl.networkState !== mediaEl.NETWORK_LOADING) {
        // setSrc() alone doesn't always (re)start the resource-selection
        // algorithm on iOS Safari for dynamically-set blob URLs — kick it.
        mediaEl.load();
      }
    }, 100);

    // Hard timeout: if nothing fires after 15s the file is truly unplayable
    const errorTimer = setTimeout(() => {
      if (!isReadyRef.current) {
        const mediaEl = ws.getMediaElement() as HTMLAudioElement | null;
        const diag = `type=${fileType || "unknown"}, readyState=${mediaEl?.readyState ?? "n/a"}, networkState=${mediaEl?.networkState ?? "n/a"}, error=${mediaEl?.error?.code ?? "none"}`;
        console.error("Audio failed to become ready after 15s:", diag);
        setLoadError(`Failed to load audio. The file may be unsupported or corrupted. (${diag})`);
        setIsLoading(false);
      }
    }, 15000);

    // The <audio> element's own "error" event carries a MediaError with a
    // code (1-4). If the browser fundamentally can't decode/play this file
    // (codes 3-4), fail fast with a clearer message instead of waiting 15s.
    const mediaEl = ws.getMediaElement() as HTMLAudioElement | null;
    const onMediaError = () => {
      const mediaError = mediaEl?.error;
      console.error("Audio element error:", mediaError?.code, mediaError?.message);
      if (isReadyRef.current || !mediaError) return;
      if (
        mediaError.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED ||
        mediaError.code === MediaError.MEDIA_ERR_DECODE
      ) {
        clearTimeout(fallbackTimer);
        clearTimeout(errorTimer);
        setLoadError(`This audio format isn't supported by your browser. Try converting the file to MP3 or WAV and uploading again. (type=${fileType || "unknown"}, error=${mediaError.code})`);
        setIsLoading(false);
      }
    };
    mediaEl?.addEventListener("error", onMediaError);

    return () => {
      clearTimeout(fallbackTimer);
      clearTimeout(errorTimer);
      mediaEl?.removeEventListener("error", onMediaError);
      containerRef.current?.removeEventListener("touchstart", handleTouchStart);
      isReadyRef.current = false;

      stopRafLoop();
      if (grainPlayerRef.current) {
        grainPlayerRef.current.stop();
        grainPlayerRef.current.dispose();
        grainPlayerRef.current = null;
      }
      decodedBufferRef.current = null;
      engineModeRef.current = "native";
      playStartCtxTimeRef.current = 0;
      playStartOffsetRef.current = 0;
      playStartRateRef.current = 1;
      grainPositionRef.current = 0;
      decodeFallbackWarnedRef.current = false;
      engineSwitchErrorWarnedRef.current = false;

      ws.destroy();
    };
  }, [audioUrl]);

  // Combined tempo/pitch effect. GrainPlayer is only needed for pitch
  // shifting (independent pitch + tempo). Tempo-only changes stay in native
  // mode so the browser's WSOLA pitch-preservation is used — much cleaner
  // than granular synthesis for music content.
  useEffect(() => {
    if (!isReady || !wsRef.current) return;
    const needsGrain = pitch !== 0;

    if (needsGrain && engineModeRef.current === "native") {
      switchToGrainEngine().then(() => {
        const gp = grainPlayerRef.current;
        if (gp && engineModeRef.current === "grain") {
          gp.playbackRate = tempo;
          gp.detune = pitch * 100;
        } else {
          // Decode-failure fallback, or switch didn't complete: stay
          // native, apply tempo via WSOLA so it's never a no-op.
          applyTempo(wsRef.current!, tempo);
        }
      }).catch((err) => {
        if (!engineSwitchErrorWarnedRef.current) {
          engineSwitchErrorWarnedRef.current = true;
          console.error("Failed to switch to GrainPlayer engine, falling back to native playbackRate:", err);
        }
        applyTempo(wsRef.current!, tempo);
      });
      return;
    }

    if (engineModeRef.current === "grain") {
      const gp = grainPlayerRef.current;
      if (gp) {
        rebasePositionTracking(gp);
        gp.playbackRate = tempo;
        gp.detune = pitch * 100;
      }
      return;
    }

    // Native mode, tempo-only — use browser's pitch-preserved playback rate
    applyTempo(wsRef.current!, tempo);
  }, [tempo, pitch, isReady, switchToGrainEngine, applyTempo]);

  useEffect(() => {
    onSettingsChange?.({ tempo, pitch, loopStart, loopEnd });
  }, [tempo, pitch, loopStart, loopEnd]);

  const handlePlayPause = async () => {
    if (!wsRef.current) return;
    // Warm up the shared Tone audio context from this direct user gesture,
    // so a later tempo/pitch-triggered switchToGrainEngine() doesn't hit an
    // autoplay-policy rejection when it awaits Tone.start() from an effect.
    Tone.start().catch(() => {});
    if (engineModeRef.current === "grain") {
      if (isPlaying) {
        stopGrainPlayback(grainPositionRef.current);
      } else {
        let pos = grainPositionRef.current;
        if (loopEnabled && loopStart !== null && currentTime >= (loopEnd ?? Infinity)) {
          pos = loopStart;
        }
        // Re-play the media element from this user gesture to re-establish
        // the iOS Playback audio session before the grain engine starts.
        wsRef.current.play().catch(() => {});
        startGrainPlayback(pos);
      }
      return;
    }
    if (isPlaying) {
      wsRef.current.pause();
    } else {
      if (loopEnabled && loopStart !== null && currentTime >= (loopEnd ?? Infinity)) {
        wsRef.current.setTime(loopStart);
      }
      wsRef.current.play();
    }
  };

  const handleSkipBack = () => {
    if (!wsRef.current) return;
    const target = Math.max(0, currentTime - 5);
    if (engineModeRef.current === "grain") {
      seekGrainTo(target);
    } else {
      wsRef.current.setTime(target);
    }
  };

  const handleSkipForward = () => {
    if (!wsRef.current) return;
    const target = Math.min(duration, currentTime + 5);
    if (engineModeRef.current === "grain") {
      seekGrainTo(target);
    } else {
      wsRef.current.setTime(target);
    }
  };

  const handleRestart = () => {
    if (!wsRef.current) return;
    const target = loopEnabled && loopStart !== null ? loopStart : 0;
    if (engineModeRef.current === "grain") {
      seekGrainTo(target);
    } else {
      wsRef.current.setTime(target);
    }
  };

  const handleSetLoopA = () => {
    if (!wsRef.current || !regionsRef.current) return;
    const t = engineModeRef.current === "grain"
      ? grainPositionRef.current
      : wsRef.current.getCurrentTime();
    setLoopStart(t);

    if (loopEnd !== null && t < loopEnd) {
      // Both points now valid — create/update region and activate loop
      if (regionRef.current) {
        regionRef.current.setOptions({ start: t, end: loopEnd });
      } else {
        const region = regionsRef.current.addRegion({
          start: t,
          end: loopEnd,
          color: "rgba(99, 102, 241, 0.25)",
          drag: true,
          resize: true,
        });
        regionRef.current = region;
      }
      setLoopEnabled(true);
    } else {
      // B not set yet, or A was placed after B — clear region and wait
      if (regionRef.current) {
        regionRef.current.remove();
        regionRef.current = null;
      }
      if (loopEnd !== null && t >= loopEnd) setLoopEnd(null);
      setLoopEnabled(false);
    }
  };

  const handleSetLoopB = () => {
    if (!wsRef.current || !regionsRef.current) return;
    const t = engineModeRef.current === "grain"
      ? grainPositionRef.current
      : wsRef.current.getCurrentTime();
    setLoopEnd(t);

    if (loopStart !== null && loopStart < t) {
      // Both points now valid — create/update region and activate loop
      if (regionRef.current) {
        regionRef.current.setOptions({ start: loopStart, end: t });
      } else {
        const region = regionsRef.current.addRegion({
          start: loopStart,
          end: t,
          color: "rgba(99, 102, 241, 0.25)",
          drag: true,
          resize: true,
        });
        regionRef.current = region;
      }
      setLoopEnabled(true);
    } else {
      // A not set yet, or B was placed before A — clear region and wait
      if (regionRef.current) {
        regionRef.current.remove();
        regionRef.current = null;
      }
      if (loopStart !== null && loopStart >= t) setLoopStart(null);
      setLoopEnabled(false);
    }
  };

  const handleClearLoop = () => {
    if (regionRef.current) {
      regionRef.current.remove();
      regionRef.current = null;
    }
    setLoopStart(null);
    setLoopEnd(null);
    setLoopEnabled(false);
  };

  const handleToggleLoop = () => {
    if (loopStart === null || loopEnd === null) return;
    setLoopEnabled((v) => !v);
  };

  const tempoPercent = Math.round(tempo * 100);
  const loopActive = loopEnabled && loopStart !== null && loopEnd !== null;

  return (
    <div className="flex flex-col gap-4">
      {fileName && (
        <p className="text-sm text-muted-foreground truncate" data-testid="text-audio-filename">
          {fileName}
        </p>
      )}

      <div
        ref={containerRef}
        className="rounded-lg overflow-hidden bg-muted/30 border border-border cursor-pointer"
        style={{ minHeight: 100 }}
        data-testid="audio-waveform"
        onTouchMove={(e) => { if (e.touches[0]) seekFromTouch(e.touches[0]); }}
        onTouchEnd={(e) => { if (e.changedTouches[0]) seekFromTouch(e.changedTouches[0]); }}
      />

      {isLoading && !loadError && (
        <div className="text-center text-muted-foreground text-sm py-2">Loading audio…</div>
      )}
      {loadError && (
        <div className="text-center text-destructive text-sm py-2">{loadError}</div>
      )}

      <div className="flex items-center justify-between text-xs text-muted-foreground font-mono px-1">
        <span data-testid="text-current-time">{formatTime(currentTime)}</span>
        <span data-testid="text-duration">{formatTime(duration)}</span>
      </div>

      <div className="flex items-center justify-center gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" onClick={handleRestart} data-testid="button-audio-restart" disabled={!isReady}>
              <RotateCcw className="w-4 h-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Restart</TooltipContent>
        </Tooltip>

        <Button variant="ghost" size="icon" onClick={handleSkipBack} data-testid="button-audio-skip-back" disabled={!isReady}>
          <Rewind className="w-5 h-5" />
        </Button>

        <Button
          size="icon"
          className="w-12 h-12 rounded-full"
          onClick={handlePlayPause}
          data-testid="button-audio-play-pause"
          disabled={!isReady}
        >
          {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
        </Button>

        <Button variant="ghost" size="icon" onClick={handleSkipForward} data-testid="button-audio-skip-forward" disabled={!isReady}>
          <FastForward className="w-5 h-5" />
        </Button>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={loopActive ? "default" : "ghost"}
              size="icon"
              onClick={handleToggleLoop}
              data-testid="button-audio-loop-toggle"
              disabled={!isReady || loopStart === null || loopEnd === null}
            >
              <Repeat className="w-4 h-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{loopActive ? "Loop on" : "Loop off"}</TooltipContent>
        </Tooltip>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium">Tempo</label>
              <p className="text-xs text-muted-foreground">Speed · pitch preserved</p>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-mono text-primary" data-testid="text-tempo-value">{tempoPercent}%</span>
              {tempo !== 1 && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="w-5 h-5"
                  onClick={() => setTempo(1)}
                  data-testid="button-tempo-reset"
                >
                  <RotateCcw className="w-3 h-3" />
                </Button>
              )}
            </div>
          </div>
          <Slider
            min={50}
            max={150}
            step={1}
            value={[tempoPercent]}
            onValueChange={([v]) => {
              // Warm up the Tone context from this gesture so the
              // tempo effect's switchToGrainEngine() can rely on
              // Tone.start() already being resolved.
              Tone.start().catch(() => {});
              setTempo(v / 100);
            }}
            data-testid="slider-tempo"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>50%</span>
            <span>100%</span>
            <span>150%</span>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium">Pitch</label>
              <p className="text-xs text-muted-foreground">Semitones · tempo unchanged</p>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-mono text-primary" data-testid="text-pitch-value">
                {pitch > 0 ? `+${pitch}` : pitch} st
              </span>
              {pitch !== 0 && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="w-5 h-5"
                  onClick={() => setPitch(0)}
                  data-testid="button-pitch-reset"
                >
                  <RotateCcw className="w-3 h-3" />
                </Button>
              )}
            </div>
          </div>
          <Slider
            min={-6}
            max={6}
            step={1}
            value={[pitch]}
            onValueChange={([v]) => {
              Tone.start().catch(() => {});
              setPitch(v);
            }}
            data-testid="slider-pitch"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>-6</span>
            <span>0</span>
            <span>+6</span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 pt-1">
        <span className="text-sm font-medium mr-1">Loop</span>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={loopStart !== null ? "default" : "outline"}
              size="sm"
              className="w-9 h-9 p-0 font-bold"
              onClick={handleSetLoopA}
              data-testid="button-loop-a"
              disabled={!isReady}
            >
              A
            </Button>
          </TooltipTrigger>
          <TooltipContent>Set loop start to current time{loopStart !== null ? ` (${formatTime(loopStart)})` : ""}</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={loopEnd !== null ? "default" : "outline"}
              size="sm"
              className="w-9 h-9 p-0 font-bold"
              onClick={handleSetLoopB}
              data-testid="button-loop-b"
              disabled={!isReady}
            >
              B
            </Button>
          </TooltipTrigger>
          <TooltipContent>Set loop end to current time{loopEnd !== null ? ` (${formatTime(loopEnd)})` : ""}</TooltipContent>
        </Tooltip>

        {(loopStart !== null || loopEnd !== null) && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="w-9 h-9 p-0 text-muted-foreground"
                onClick={handleClearLoop}
                data-testid="button-loop-clear"
              >
                ✕
              </Button>
            </TooltipTrigger>
            <TooltipContent>Clear loop points</TooltipContent>
          </Tooltip>
        )}

        {loopStart !== null && loopEnd !== null ? (
          <span className="text-xs text-muted-foreground ml-1">
            {formatTime(loopStart)} – {formatTime(loopEnd)}
            {loopActive ? " · looping" : " · paused"}
          </span>
        ) : loopStart !== null ? (
          <span className="text-xs text-muted-foreground ml-1">A: {formatTime(loopStart)} · press B to set end</span>
        ) : loopEnd !== null ? (
          <span className="text-xs text-muted-foreground ml-1">B: {formatTime(loopEnd)} · press A to set start</span>
        ) : null}
      </div>
    </div>
  );
}
